import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Search, Plus, List, SlidersHorizontal, PenTool, Network } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { HeroSection } from '@/components/HeroSection';
import { ResourceGraph } from '@/components/ResourceGraph';
import { ResourceCard } from '@/components/ResourceCard';
import { CategoryFilter } from '@/components/CategoryFilter';
import { AddResourceDialog } from '@/components/AddResourceDialog';
import { NetworkStats } from '@/components/NetworkStats';
import { EditResourcesTab } from '@/components/EditResourcesTab';

import { useResources } from '@/contexts/ResourceContext';
import type { Resource } from '@/contexts/ResourceContext';

export default function IndexPage() {
  // SEO Meta Tags
  useSeoMeta({
    title: 'Regenerate Community Kāeo Whangaroa Mutual Aid | Living Map of Mutual Aid',
    description:
      'A living map of community resilience where neighbours share resources and grow stronger together. Find and contribute mutual aid resources in your community.',
    ogTitle: 'Regenerate Community Kāeo Whangaroa Mutual Aid',
    ogDescription:
      'A living map of mutual aid — where neighbours share resources, support each other, and build resilience together.',
    ogType: 'website',
    ogImage: 'https://blossom.primal.net/b048c87e57fdafc8230b82ade0a189150b95d84218b4efc64d7d9c9bad8b970b.png',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Regenerate Community Kāeo Whangaroa Mutual Aid',
    twitterDescription:
      'A living map of mutual aid — where neighbours share resources, support each other, and build resilience together.',
    twitterImage: 'https://blossom.primal.net/b048c87e57fdafc8230b82ade0a189150b95d84218b4efc64d7d9c9bad8b970b.png',
  });

  const {
    filteredResources,
    searchQuery,
    setSearchQuery,
    categoryFilters,
    setCategoryFilters,
  } = useResources();

  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'graph' | 'edit'>('list');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const handleResourceSelect = (resource: Resource) => {
    setSelectedResource(resource);
    // Switch to graph view when selecting a resource from list
    if (viewMode !== 'graph') {
      setViewMode('graph');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <HeroSection />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 pb-16 space-y-12">
        {/* Network Stats */}
        <section className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <NetworkStats />
        </section>

        {/* Search and Filters */}
        <section className="space-y-6 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                placeholder="Search resources, tags, or descriptions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 text-base shadow-sm"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-2"
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </Button>
              )}
            </div>

            {/* Add Resource Button - Disabled until backend is ready */}
            <Button
              size="lg"
              className="h-12 gap-2 shadow-lg opacity-50 cursor-not-allowed"
              disabled
            >
              <Plus className="h-5 w-5" />
              Add Resource (Soon)
            </Button>

            {/* Mobile Filters */}
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild className="sm:hidden">
                <Button variant="outline" size="lg" className="h-12 gap-2">
                  <SlidersHorizontal className="h-5 w-5" />
                  Filters
                  {categoryFilters.length > 0 && (
                    <Badge variant="secondary" className="ml-1">
                      {categoryFilters.length}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <div className="py-6">
                  <h2 className="text-lg font-semibold mb-4">Filter Resources</h2>
                  <CategoryFilter
                    selected={categoryFilters}
                    onChange={setCategoryFilters}
                  />
                </div>
              </SheetContent>
            </Sheet>

            {/* Desktop Filters */}
            <div className="hidden sm:block w-64">
              <CategoryFilter
                selected={categoryFilters}
                onChange={setCategoryFilters}
              />
            </div>
          </div>

          {/* Active filters display */}
          {categoryFilters.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              <div className="flex flex-wrap gap-2">
                {categoryFilters.map((category) => (
                  <Badge
                    key={category}
                    variant="secondary"
                    className="cursor-pointer hover:bg-destructive/10"
                    onClick={() =>
                      setCategoryFilters(categoryFilters.filter((c) => c !== category))
                    }
                  >
                    {category} ×
                  </Badge>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs"
                  onClick={() => setCategoryFilters([])}
                >
                  Clear all
                </Button>
              </div>
            </div>
          )}
        </section>

        {/* Map and List View */}
        <section className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
           <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'list' | 'graph' | 'edit')}>
             <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6">
               <div>
                 {viewMode === 'edit' ? (
                   <h2 className="text-xl sm:text-2xl font-bold">Edit Resources</h2>
                 ) : (
                   <h2 className="text-xl sm:text-2xl font-bold">
                     {filteredResources.length} Resource{filteredResources.length !== 1 ? 's' : ''} Found
                   </h2>
                 )}
                 <p className="text-sm text-muted-foreground mt-1">
                   {viewMode === 'edit' 
                     ? 'Manage, verify, and remove community resources'
                     : viewMode === 'graph'
                     ? 'Visual network of resources and their connections'
                     : categoryFilters.length > 0 || searchQuery
                       ? 'Filtered results from the community network'
                       : 'All resources in your community'}
                 </p>
               </div>
             </div>

{/* Mobile Tabs - visible only on small screens - Edit disabled until backend ready */}
              <div className="sm:hidden mb-4">
                <TabsList className="w-full grid grid-cols-2">
                  <TabsTrigger value="list" className="gap-1.5 text-xs">
                    <List className="h-4 w-4" />
                    List
                  </TabsTrigger>
                  <TabsTrigger value="graph" className="gap-1.5 text-xs">
                    <Network className="h-4 w-4" />
                    Graph
                  </TabsTrigger>
                  {/* Edit tab disabled until backend ready */}
                </TabsList>
              </div>

              {/* Desktop Tabs - hidden on small screens - Edit disabled until backend ready */}
              <TabsList className="hidden sm:flex mb-6">
                <TabsTrigger value="list" className="gap-2">
                  <List className="h-4 w-4" />
                  List
                </TabsTrigger>
                <TabsTrigger value="graph" className="gap-2">
                  <Network className="h-4 w-4" />
                  Graph
                </TabsTrigger>
                {/* Edit tab disabled until backend ready */}
              </TabsList>

              <TabsContent value="list" className="mt-0">
                {filteredResources.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-12 px-6 sm:py-16 sm:px-8">
                      <div className="text-center max-w-sm mx-auto space-y-4">
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-muted mx-auto flex items-center justify-center">
                          <Search className="h-6 w-6 sm:h-8 sm:w-8 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold">No resources found</h3>
                        <p className="text-sm sm:text-muted-foreground">
                          {searchQuery || categoryFilters.length > 0
                            ? 'Try adjusting your search or filters'
                            : 'Be the first to add a resource to your community!'}
                        </p>
                        <Button disabled className="gap-2 w-full sm:w-auto opacity-50 cursor-not-allowed">
                          <Plus className="h-4 w-4" />
                          Coming Soon
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:gap-6">
                    {filteredResources.map((resource) => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        onSelect={handleResourceSelect}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

            <TabsContent value="graph" className="mt-0">
               <div className="sm:h-[500px] md:h-[600px]">
                 <ResourceGraph
                   resources={filteredResources}
                   onResourceClick={handleResourceSelect}
                   selectedId={selectedResource?.id}
                 />
               </div>
            </TabsContent>

            <TabsContent value="edit" className="mt-0">
              <EditResourcesTab />
            </TabsContent>
          </Tabs>
        </section>

        {/* About section */}
        <section className="animate-fade-in-up relative" style={{ animationDelay: '0.8s' }}>
          {/* Background image */}
          <div 
            className="absolute inset-0 opacity-[0.30] bg-cover bg-center bg-no-repeat pointer-events-none"
            style={{ backgroundImage: 'url(https://blossom.dreamith.to/689eb3263971fe049932128f52265a671ee094cad8f7461489e0633d8151d90c.jpeg)' }}
          />
          <Card className="border-0 shadow-lg overflow-hidden relative bg-background/80 backdrop-blur-sm">
            <div className="h-2 bg-gradient-to-r from-primary via-accent to-primary" />
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl font-bold mb-4">About Regenerate Community Kāeo Whangaroa Mutual Aid</h2>
                  
                  <div className="bg-secondary/50 rounded-xl p-4 mb-4">
                    <h3 className="font-semibold mb-2">What is Mutual Aid?</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Mutual aid is community support where neighbours help each other directly — 
                      not through governments or charities, but neighbour to neighbour. It's 
                      <span className="font-medium text-foreground"> reciprocal</span> (everyone gives and receives), 
                      <span className="font-medium text-foreground"> community-led</span> (people decide what they need), and 
                      about <span className="font-medium text-foreground"> solidarity</span> (building relationships, not just handing out resources).
                    </p>
                  </div>

                  <p className="text-muted-foreground leading-relaxed mb-4">
                    Regenerate Community Kāeo Whangaroa Mutual Aid is a living map of mutual aid — a place where
                    neighbours share resources, support each other, and build resilience together.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Every resource you add helps someone in need find support. Every verification
                    makes the network stronger. Together, we're growing a more caring, connected
                    community.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Button variant="outline" disabled className="gap-2 opacity-50 cursor-not-allowed">
                      <Plus className="h-4 w-4" />
                      Add a Resource (Soon)
                    </Button>
                  </div>
                </div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl blur-xl" />
                  <div className="relative grid grid-cols-2 gap-4">
                    {[
                      { icon: '🤝', label: 'Give & Receive' },
                      { icon: '🌱', label: 'Community-Led' },
                      { icon: '💪', label: 'Build Together' },
                      { icon: '❤️', label: 'Solidarity' },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className="p-4 rounded-xl bg-secondary/50 text-center space-y-2"
                      >
                        <span className="text-3xl">{item.icon}</span>
                        <p className="text-sm font-medium">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t border-border/50 py-8 mt-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-12 h-12 rounded-full overflow-hidden border border-border">
                <img
                  src="https://blossom.primal.net/88b784fe3b73f35b2f8db64a798c06d79fa1e40c1e77748d4d15b8ef55fb63b7.png"
                  alt="Regenerate Community Kāeo Whangaroa Mutual Aid Logo"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-semibold">Regenerate Community Kāeo Whangaroa</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Built with care for communities everywhere
            </p>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>Vibed with</span>
              <a
                href="https://shakespeare.diy"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold hover:text-primary transition-colors"
              >
                Shakespeare
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Add Resource Dialog */}
      <AddResourceDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
    </div>
  );
}
